import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, ChevronDown, HeartPulse, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 pr-9 text-[13px] text-[#1A2622] outline-none cursor-pointer focus:border-[#A06628]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]" />
    </div>
  );
}

export default function HospicePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [state, setState] = useState(params.get('state') || '');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const LIMIT = 40;

  useEffect(() => { setPage(0); }, [q, state]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.hospice({
        q: q || undefined,
        state: state || undefined,
        limit: LIMIT,
        offset: page * LIMIT,
      }).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, state, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Hospice' }]} />

        <header className="mt-2 mb-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wider bg-[#FBE6CC] text-[#A06628]">
              <HeartPulse size={11} /> Post-acute layer
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-[#1A2622] mt-2">Hospice Agencies</h1>
            <p className="mt-2 text-[13px] text-[#5A6660] max-w-[640px]">
              Every CMS-certified <strong>hospice</strong> in the U.S. Surface ownership, certification, and proximity to nursing homes &amp; hospitals.
            </p>
          </div>
          <Link to="/home-health" className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#2D5F4F]" data-testid="link-home-health">
            View Home Health →
          </Link>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="relative md:col-span-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A857F]" />
            <input
              data-testid="hospice-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search hospice name…"
              className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-9 pr-3 text-[13px] outline-none focus:border-[#A06628]"
            />
          </div>
          <Select testId="hospice-filter-state" value={state} onChange={setState} options={US_STATES} placeholder="State" />
        </div>

        <div className="flex items-center gap-2 mb-3 text-[12.5px] text-[#7A857F]">
          <span data-testid="hospice-result-count">{fmtInt(data.total)} hospices</span>
        </div>

        <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Hospice</th>
                  <th className="text-left px-4 py-2.5">City / State</th>
                  <th className="text-left px-4 py-2.5">Ownership</th>
                  <th className="text-left px-4 py-2.5">Profit status</th>
                  <th className="text-left px-4 py-2.5">Phone</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#7A857F]">Loading…</td></tr>}
                {!loading && data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#7A857F]">No hospices match these filters.</td></tr>}
                {!loading && data.items.map((a) => (
                  <tr key={a.ccn} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC] cursor-pointer" onClick={() => navigate(`/hospice/${a.ccn}`)} data-testid={`hospice-list-${a.ccn}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/hospice/${a.ccn}`} className="text-[#1A2622] font-medium hover:text-[#A06628]">{a.name}</Link>
                      <div className="text-[10.5px] text-[#7A857F]">CCN {a.ccn} · {a.address || ''}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{a.city || '—'}, {a.state || ''}</td>
                    <td className="px-4 py-2.5 text-[#3A4A44]"><div className="truncate max-w-[180px]" title={a.ownership}>{a.ownership || '—'}</div></td>
                    <td className="px-4 py-2.5 text-[#3A4A44]">{a.profit_status || '—'}</td>
                    <td className="px-4 py-2.5 text-[11px] text-[#7A857F]">{a.phone || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#F0E7D2] text-[12px]">
            <span className="text-[#7A857F]">Page {page + 1} of {totalPages}</span>
            <div className="flex gap-2">
              <button data-testid="hospice-prev" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 h-8 rounded border border-[#E5DCC4] disabled:opacity-40">Prev</button>
              <button data-testid="hospice-next" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 h-8 rounded border border-[#E5DCC4] disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
          <Info size={12} className="mt-0.5 shrink-0" />
          <span><strong>Source:</strong> {data.source || 'CMS Hospice General Information (yc9t-dgbk)'}. Updated quarterly by CMS.</span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
